<template>
    <div style="margin-bottom:20px" ref="container">

        <div class="headline-row">
            <div class="headline-col">
                <h2>Hiring Area</h2>
            </div>
            <div class="headline-col headline-col-btns">
                <div style="display: flex">
                    <import-area 
                        v-if="areaId && isTrUser && (activeTab === 'map' || activeTab === 'cities' || activeTab === 'locations')"
                        :area-id="areaId"
                        @process="refreshArea"
                    ></import-area>

                    <el-button type="warning"
                               plain
                               v-bind:disabled="!initialized"
                               @click="toggleEdit(true)"
                               v-show="areaId && isTrUser && activeTab === 'map'">
                        <i class="fa fa-edit"></i> Edit map
                    </el-button>
                </div>
            </div>
        </div>

        <el-row>
            <el-col :span="24">

                <el-alert v-if="errorMessage" type="error" title="Error: ">{{ errorMessage }}</el-alert>
                <el-tabs v-model="activeTab" v-else @tab-click="handleTabClick">
                    <el-tab-pane name="zip">
                        <span slot="label">ZIP codes<span v-if="hasError" class="label label-danger" style="position:relative;top:-3px;left:5px;">no zip codes</span></span>
                        <el-form-item prop="zipCodes" label="ZIP codes">
                            <el-input type="textarea" v-model="zipCodes" rows="8" @change="refreshMapFromZipCodes" disabled />
                        </el-form-item>
                    </el-tab-pane>
                    <el-tab-pane label="Map" name="map">
                        <div v-loading="loadingMap" style="min-height: 250px;">
                            <div class="map-wrapper" ref="mapWrapper" v-show="initialized">
                            <div class="map-controls">
                                <el-button type="default" @click="showCityDialog = true" size="small">
                                    <i :class="['fa', eraseModeOn ? 'fa-minus' : 'fa-plus']"
                                       aria-hidden="true"></i> Zip Code Radius
                                </el-button>
                                <el-button type="default" @click="showZipCodeDialog = true" size="small">
                                    <i :class="['fa', eraseModeOn ? 'fa-minus' : 'fa-plus']"
                                       aria-hidden="true"></i> Specific ZIP Codes
                                </el-button>
                                <el-button type="default" @click="showZipGroupDialog = true" size="small">
                                    <i :class="['fa', eraseModeOn ? 'fa-minus' : 'fa-plus']" aria-hidden="true"></i> Zip Groups
                                </el-button>
                                <el-button type="default" @click="showMultipleCitiesDialog = true" size="small">
                                    <i :class="['fa', eraseModeOn ? 'fa-minus' : 'fa-plus']"
                                       aria-hidden="true"></i> Multiple Cities
                                </el-button>
                                <el-button type="default"
                                           v-if="gmapsObj !== null && map !== null"
                                           @click="showCityRadiusDialog = true"
                                           size="small">
                                    <i :class="['fa', eraseModeOn ? 'fa-minus' : 'fa-plus']"
                                       aria-hidden="true"></i> City or State
                                </el-button>

                                <el-button type="danger" size="small" @click="clearAll" style="margin-right: 1.5em">
                                    <i class="fas fa-trash" aria-hidden="true"></i> Clear All
                                </el-button>
                                <el-tooltip content="Toggle Adding/Removing Mode" placement="top">
                                    <el-switch v-model="eraseModeOn"
                                               style="padding: 0 10px"
                                               active-text="REMOVING"
                                               active-color="#e64e42"
                                               inactive-text="ADDING">
                                    </el-switch>
                                </el-tooltip>

                                <el-input id="pac-input" 
                                          v-model="location" 
                                          placeholder="Enter a location" 
                                          size="small"
                                          style="width: 250px; margin-left: 20px;" 
                                ></el-input>

                                <el-button type="danger"
                                           class="is-aligned-right"
                                           size="small"
                                           @click="toggleEdit"
                                           plain>
                                    <i class="fa fa-times" aria-hidden="true"></i> Close
                                </el-button>
                                <span class="processing-changes-indicator" v-if="processingChanges">
                                    <i class="fa fa-pulse fa-spinner"></i> Processing
                                </span>
                            </div>
                            <div class="map-container">
                                <div class="map-states" v-if="stateBox && (stateBox.inclusions || stateBox.exclusions)">
                                    <template v-if="stateBox.inclusions">
                                        <h3>States</h3>
                                        <ul>
                                            <li v-for="(state, index) in stateBox.inclusions" :key="`state_${index}`">
                                                <i class="fa fa-map-marker" aria-hidden="true"></i> 

                                                <i v-if="state.name == 'Nationwide'">{{ state.name }}</i>
                                                <template v-else>{{ state.name }}</template>

                                                <a :disabled="state.removing"
                                                    href="#"
                                                    class="is-aligned-right text-danger"
                                                    @click.prevent="removeLocation(state)">
                                                    <i :class="['fas', state.removing ? 'fa-pulse fa-spinner' : 'fa-trash']"></i>
                                                </a>
                                            </li>
                                        </ul>
                                    </template>

                                    <template v-if="stateBox.exclusions">
                                        <h3>Excluded States</h3>
                                        <ul>
                                            <li v-for="(state, index) in stateBox.exclusions" :key="`state_${index}`">
                                                <i class="fa fa-map-marker" aria-hidden="true"></i> 

                                                <i v-if="state.name == 'Nationwide'">{{ state.name }}</i>
                                                <template v-else>{{ state.name }}</template>

                                                <a :disabled="state.removing"
                                                    href="#"
                                                    class="is-aligned-right text-danger"
                                                    @click.prevent="removeLocation(state)">
                                                    <i :class="['fas', state.removing ? 'fa-pulse fa-spinner' : 'fa-trash']"></i>
                                                </a>
                                            </li>
                                        </ul>
                                    </template>
                                </div>

                                <div class="map-locations" v-if="locationBox && (locationBox.inclusions || locationBox.exclusions)">
                                    <div v-for="(locations, radius) in locationBox.inclusions" :key="`inclusion_radius_${radius}`" style="margin-bottom: 30px">
                                        <h3>
                                            Locations + {{ radius }}mi
                                            <RadiusChange v-if="areaId"
                                                            :area-id="areaId"
                                                            :is-exclusion="false"
                                                            :radius="parseInt(radius)"
                                                            @process="refreshArea"
                                                            />
                                        </h3>
                                        <ul>
                                            <li v-for="location in locations" :key="`location_${location.id}`" style="display: flex; justify-content: space-between">
                                                <div>
                                                    <i class="fa fa-map-marker" aria-hidden="true"></i> 
                                                    <template v-if="location.city">{{ location.city }}, {{ location.state_code }}</template>
                                                    <template v-else>{{ location.zip_code }}</template>
                                                </div>

                                                <div>
                                                    <RadiusChangeLocation 
                                                        v-if="areaId"
                                                        :area-location-id="location.id"
                                                        :location="location"
                                                        :radius="parseInt(radius)"
                                                        @process="refreshArea"
                                                    />

                                                    <a :disabled="location.removing"
                                                        href="#"
                                                        class="text-danger"
                                                        @click.prevent="removeLocation(location)"
                                                        style="margin-left: 10px"
                                                    >
                                                        <i :class="['fas', location.removing ? 'fa-pulse fa-spinner' : 'fa-trash']"></i>
                                                    </a>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>

                                    <div v-for="(locations, radius) in locationBox.exclusions" :key="`exclusion_radius_${radius}`" style="margin-bottom: 30px">
                                        <h3>
                                            Excl. Locations + {{ radius }}mi
                                            <RadiusChange v-if="areaId"
                                                            :area-id="areaId"
                                                            :is-exclusion="true"
                                                            :radius="parseInt(radius)"
                                                            @process="refreshArea"/>
                                        </h3>
                                        <ul>
                                            <li v-for="location in locations" :key="`location_${location.id}`" style="display: flex; justify-content: space-between">
                                                <div>
                                                    <i class="fa fa-map-marker" aria-hidden="true"></i> 
                                                    <template v-if="location.city">{{ location.city }}, {{ location.state_code }}</template>
                                                    <template v-else>{{ location.zip_code }}</template>
                                                </div>

                                                <div>
                                                    <RadiusChangeLocation 
                                                        v-if="areaId"
                                                        :area-location-id="location.id"
                                                        :location="location"
                                                        :radius="parseInt(radius)"
                                                        @process="refreshArea"
                                                    />

                                                    <a :disabled="location.removing"
                                                        href="#"
                                                        class="text-danger"
                                                        @click.prevent="removeLocation(location)"
                                                        style="margin-left: 10px"
                                                    >
                                                        <i :class="['fas', location.removing ? 'fa-pulse fa-spinner' : 'fa-trash']"></i>
                                                    </a>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="map-canvas" ref="mapCanvas"></div>
                            </div>
                        </div>
                        </div>
                    </el-tab-pane>
                    <el-tab-pane name="cities" v-loading="true">
                        <span slot="label">Cities</span>
                        <div v-loading="loadingCitiesList">
                            <el-form-item prop="citiesList" label="Cities">
                                <el-input type="textarea" v-model="citiesList" rows="8" :readonly="true"/>
                            </el-form-item>
                        </div>
                    </el-tab-pane>
                    <el-tab-pane name="locations">
                        <span slot="label">Locations</span>

                        <map-locations
                            v-if="gmapsObj !== null"
                            :gmaps-obj="gmapsObj"
                            :map="map"
                            :area-id="areaId"
                            :area-locations="areaLocations"
                            :is-area-dirty="refreshingArea"
                            :list="list"
                            @process="refreshArea"
                        ></map-locations>
                    </el-tab-pane>
                </el-tabs>

            </el-col>
        </el-row>

        <CityModal v-if="sessionId && areaId"
                      :open-dialog.sync="showCityDialog"
                      :erase-mode-on="eraseModeOn"
                      :area-id="areaId"
                      :mapUrl="mapUrl"
                      @process="refreshArea"/>
        <ZipCodeModal v-if="sessionId && areaId"
                      :open-dialog.sync="showZipCodeDialog"
                      :erase-mode-on="eraseModeOn"
                      :area-id="areaId"
                      :mapUrl="mapUrl"
                      @process="refreshArea"/>

        <ZipGroupModal v-if="sessionId && areaId"
                       :open-dialog.sync="showZipGroupDialog"
                       :erase-mode-on="eraseModeOn"
                       :area-id="areaId"
                       :mapUrl="mapUrl"
                       @process="refreshArea"/>

        <MultipleCitiesModal v-if="sessionId && areaId"
                             :open-dialog.sync="showMultipleCitiesDialog"
                             :erase-mode-on="eraseModeOn"
                             :area-id="areaId"
                             :mapUrl="mapUrl"
                             @process="refreshArea"/>

        <CityRadiusModal v-if="gmapsObj !== null && map !== null && sessionId && areaId"
                         :open-dialog.sync="showCityRadiusDialog"
                         :erase-mode-on="eraseModeOn"
                         :area-id="areaId"
                         :gmaps-obj="gmapsObj"
                         :map="map"
                         :mapUrl="mapUrl"
                         @process="refreshArea"/>
    </div>
</template>

<script>
import * as Sentry from '@sentry/browser';
import {gmapApiLoader} from './helpers/gmap-api-loader.js';
import {getDrawingDefaults, getMapDefaults, getNormalizedCoord} from './helpers/functions';
import ShapeDrawer from './helpers/shape-drawer';
import CityModal from './dialogs/city.vue';
import ZipCodeModal from './dialogs/zip-code.vue';
import ZipGroupModal from './dialogs/zip-group.vue';
import MultipleCitiesModal from './dialogs/multiple-cities.vue';
import CityRadiusModal from './dialogs/city-radius.vue';
import RadiusChange from './dialogs/radius-change.vue';
import RadiusChangeLocation from './dialogs/radius-change-location.vue';
import ImportArea from './import-area.vue';
import MapLocations from './map-locations.vue';

export default {
        components: {
            CityModal,
            ZipCodeModal, 
            ZipGroupModal, 
            MultipleCitiesModal, 
            CityRadiusModal, 
            RadiusChange,
            RadiusChangeLocation,
            ImportArea,
            MapLocations
        },
        props: {
            googleApiKey: { type: String, required: true },
            mapsUrl: { type: String, required: true },
            areaId: { type: Number, required: true },
            isTrUser: { type: Boolean, required: true },
            hasError: { type: Boolean, required: false },
        },
        data() {

            return {
                location: "",
                activeTab: 'zip',
                errorMessage: false,
                sessionId: null,

                gmapsObj: null,

                loadingMap: true,
                map: null,
                drawingManager: null,

                eraseModeOn: false,
                overrideTiles: [],
                tileMap: null,

                refreshingLayer: false,
                refreshingMap: false,
                refreshingZipCodes: false,
                refreshingArea: false,
                clearing: false,
                performingEvent: false,

                stateList: [],
                locationList: [],
                zipCodes: "",

                citiesList: null,
                loadingCitiesList: true,

                showCityDialog: false,
                showZipCodeDialog: false,
                showZipGroupDialog: false,
                showMultipleCitiesDialog: false,
                showCityRadiusDialog: false,

                area: null,
                areaLocations: null,

                nationwideRemoving: false,
                list: {
                    state_options: [],
                },
            };
        },
        computed: {
            initialized() { return this.gmapsObj !== null && !this.loadingMap && this.drawingManager; },
            processingChanges() {
                return this.refreshingMap ||
                    this.refreshingZipCodes ||
                    this.refreshingArea ||
                    this.clearing ||
                    this.performingEvent;
            },
            isExclusion() {
                if(! this.eraseModeOn) {
                    return 0;
                }

                return 1;
            },
            stateBox() {
                if(! this.initialized) {
                    return null;
                }

                let statesIncluded = _.filter(this.stateList, { is_exclusion: 0 });
                let statesExcluded = _.filter(this.stateList, { is_exclusion: 1 });

                return {
                    inclusions: _.isEmpty(statesIncluded) ? null : statesIncluded,
                    exclusions: _.isEmpty(statesExcluded) ? null : statesExcluded,
                }
            },
            locationBox() {
                if(! this.initialized) {
                    return null;
                }

                let locationsIncluded = _.filter(this.locationList, { is_exclusion: 0 });
                locationsIncluded = _.groupBy(_.orderBy(locationsIncluded, ["radius_miles"], ["desc"]), "radius_miles");

                let locationsExcluded = _.filter(this.locationList, { is_exclusion: 1 });
                locationsExcluded = _.groupBy(_.orderBy(locationsExcluded, ["radius_miles"], ["desc"]), "radius_miles");

                return {
                    inclusions: _.isEmpty(locationsIncluded) ? null : locationsIncluded,
                    exclusions: _.isEmpty(locationsExcluded) ? null : locationsExcluded,
                }
            }
        },
        mounted() {
            gmapApiLoader(this.googleApiKey)
                .then((gmaps) => {
                    this.gmapsObj = gmaps;
                    this.initialized && this.initMap();
                })
                .catch(err => { this.error(err.message); });

            this.refreshArea();
        },
        methods: {
            error(message) { this.errorMessage = message; },
            initMap() {
                if (this.gmapsObj === null) {
                    // The Maps API failed to load — mounted()'s .catch already surfaced the
                    // error, so just avoid crashing when a tab click lands here afterwards.
                    return;
                }

                this.map = new this.gmapsObj.Map(this.$refs.mapCanvas, getMapDefaults(this.gmapsObj));
                // Our own drawing implementation (Google removed DrawingManager in Maps JS
                // v3.65). If anything here throws, it bubbles to the caller's .catch, which
                // surfaces a clear error and keeps the map hidden (`initialized` requires
                // drawingManager).
                this.drawingManager = new ShapeDrawer(this.gmapsObj, getDrawingDefaults());

                this.initAutocomplete();

                const shapes = ['polygon', 'rectangle'];
                shapes.forEach((shapeName) => {
                    this.drawingManager.addListener(`${shapeName}complete`, (shapeObj) => {
                        this.performingEvent = true;

                        const zips = [];
                        if (shapeName === 'polygon') {
                            shapeObj.getPath()
                                    .getArray()
                                    .forEach((pathObj) => {
                                        zips.push({ lat: pathObj.lat(), lng: pathObj.lng() });
                                    });
                        } else {
                            const ne = shapeObj.getBounds()
                                               .getNorthEast();
                            const sw = shapeObj.getBounds()
                                               .getSouthWest();
                            zips.push({ lat: ne.lat(), lng: ne.lng() });
                            zips.push({ lat: ne.lat(), lng: sw.lng() });
                            zips.push({ lat: sw.lat(), lng: sw.lng() });
                            zips.push({ lat: sw.lat(), lng: ne.lng() });
                        }

                        axios.post(this.url(`admin/area/add-region`), {
                            polygon: zips,
                            area_id: this.areaId,
                            is_exclusion: this.eraseModeOn,
                        })
                             .then(response => {
                                shapeObj.setMap(null);

                                this.refreshArea();
                             })
                             .catch(err => {
                                 this.$message.error(
                                     `Ooops, something went wrong with map modification: ${err.message}`);
                             })
                             .finally(() => { this.performingEvent = false; });
                    });
                });
            },
            initAutocomplete() {
                const input = document.getElementById("pac-input");
                const options = {
                    componentRestrictions: { country: "us" },
                    fields: ["address_components", "geometry", "icon", "name"],
                    origin: this.map.getCenter(),
                    strictBounds: false,
                };
                const autocomplete = new this.gmapsObj.places.Autocomplete(input, options);
                // Set initial restriction to the greater list of countries.
                autocomplete.setComponentRestrictions({
                    country: ["us"],
                });

                const marker = new this.gmapsObj.Marker({
                    map: this.map,
                    anchorPoint: new this.gmapsObj.Point(0, -29),
                });
                autocomplete.addListener("place_changed", () => {
                    marker.setVisible(false);
                    const place = autocomplete.getPlace();

                    if (!place.geometry || !place.geometry.location) {
                        // User entered the name of a Place that was not suggested and
                        // pressed the Enter key, or the Place Details request failed.
                        return;
                    }

                    this.map.setCenter(place.geometry.location);
                    this.map.setZoom(8); 

                    marker.setPosition(place.geometry.location);
                    marker.setVisible(true);
                });

            },
            mapUrl(url) {
                return `${this.mapsUrl}${url}`;
            },
            toggleEdit() {
                this.editIsActive = !this.editIsActive;
                const bodyEl = document.body;
                if (this.editIsActive) {
                    this.$refs.mapWrapper.classList.add('edit-is-active');
                    bodyEl.style.height = '100%';
                    bodyEl.style.overflow = 'hidden';
                    this.drawingManager.setMap(this.map);
                } else {
                    this.$refs.mapWrapper.classList.remove('edit-is-active');
                    bodyEl.style.height = '';
                    bodyEl.style.overflow = '';
                    this.drawingManager.setMap(null);
                }
            },
            removeNationwide() {
                this.clearing = true;
                this.nationwideRemoving = true;

                axios.post(this.url(`admin/area/remove-nationwide`), {
                        area_id: this.areaId
                    }).then(() => {
                        this.refreshArea();
                    })
                    .finally(() => {
                        this.clearing = false;
                        this.nationwideRemoving = false;
                    });
            },
            removeLocation(areaLocation) {
                this.clearing = true;
                areaLocation.removing = true;

                axios.post(this.url(`admin/area/remove-location`), {
                        area_location_id: areaLocation.id,
                        area_id: this.areaId
                    }).then(() => {
                        this.refreshArea();
                     })
                     .finally(() => { this.clearing = false; });
            },

            refreshMapFromZipCodes() {
                if(! this.initialized) {
                    return;
                }

                this.refreshingZipCodes = true;

                axios.post(this.mapUrl(`map-tools/set-zip-codes`), {
                        zipCodes: this.zipCodes,
                        key: this.sessionId
                     }).then(response => {
                        this.overrideTiles = response.data.overrideTiles;
                         
                        const moonMapType = new this.gmapsObj.ImageMapType(this.getMapTypeOptions());
                        moonMapType.setOpacity(0.5);
                        this.map.overlayMapTypes.removeAt(0);
                        this.map.overlayMapTypes.insertAt(0, moonMapType);
                     })
                     .finally(() => { this.refreshingZipCodes = false; });
            },
            getMapTypeOptions() {
                return {
                    getTileUrl: (coord, zoom) => {
                        const normalizedCoord = getNormalizedCoord(coord, zoom);
                        if (!normalizedCoord) {
                            return null;
                        }

                        const random = Math.random();
                        if (this.overrideTiles && this.overrideTiles[zoom] &&
                            this.overrideTiles[zoom][normalizedCoord.y] &&
                            this.overrideTiles[zoom][normalizedCoord.y][normalizedCoord.x]) {

                            if (parseInt(this.overrideTiles[zoom][normalizedCoord.y][normalizedCoord.x]) === 2) {
                                return null;
                            } else {
                                return `${this.mapsUrl}map/${this.sessionId}/${zoom}/${normalizedCoord.y}/${normalizedCoord.x}.gif?v=${random}`;
                            }
                        }
                        else {
                            return null;
                        }
                    },
                    tileSize: new this.gmapsObj.Size(256, 256),
                    name: 'Job areas',
                };
            },
            clearAll() {
                this.$confirm('This will remove all zip codes from the map. Are you sure?', 'Clear map?')
                    .then(() => {
                        this.clearing = true;
                        axios.post(this.url(`admin/area/reset`), {
                                area_id: this.areaId,
                            }).then(() => {
                                this.overrideTiles = [];
                                this.refreshArea();
                            }).finally(() => { this.clearing = false; });
                    })
                    .catch(() => {});
            },
            handleTabClick() {
                if (this.activeTab === 'cities') {
                    const $this = this;
                    $this.loadingCitiesList = true;

                    axios.post(this.url('admin/area/zip-codes-to-cities'), {
                        zip_codes: this.zipCodes
                        }).then(response => {
                             const citiesArr = response.data;
                             this.citiesList = '';
                             citiesArr.forEach(el => {
                                 this.citiesList += el + '\n';
                             });
                         })
                         .finally(() => $this.loadingCitiesList = false);
                }

                if (this.activeTab === 'map' && this.sessionId === null) {
                    this.loadingMap = true;
                    axios.post(this.mapUrl('map-tools/start-session'), {
                        zip_codes: this.zipCodes
                    }).then(response => {
                        this.sessionId = response.data.session_id;
                        this.tileMap = response.data.tile_map;
                        this.overrideTiles = response.data.tile_map;
                        this.initMap();
                        this.refreshArea();
                    }).catch(err => {
                        // Map failed to initialize — show a clear error instead of a half-broken /
                        // blank map (the map stays hidden via `initialized`), and record it in Sentry.
                        this.error('The map could not be loaded. Please reload the page — if it keeps happening, contact support.');
                        Sentry.captureException(err);
                    }).finally(() => {
                        this.loadingMap = false;
                    });
                }
            },

            refreshArea(refreshMap = true) {
                if(! this.areaId) {
                    return;
                }

                this.refreshingArea = true;

                axios.get(this.url(`admin/area/${this.areaId}`)).then(response => {
                    this.area = response.data.area;
                    this.areaLocations = response.data.area_location;
                    this.stateList = response.data.states;
                    this.locationList = response.data.locations;
                    this.zipCodes = this.area.zip_codes;
                    
                    this.list.state_options = response.data.list.state_options;

                    if(refreshMap) {
                        this.refreshMapFromZipCodes();
                    }

                    this.refreshingArea = false;
                });
            }
        },
    };
</script>

<style>
    .map-controls .el-switch__label {
        color: #fff !important;
    }

    .map-controls .el-switch__label.is-active {
        color: #e64e42 !important;
    }
</style>

<style scoped>
    .headline-row {
        display: flex;
        flex-direction: row;
        margin-top: 20px;
    }

    .headline-col {
        flex-grow:1;
        align-items: flex-end;
    }

    .headline-col-btns {
        display: flex;
        justify-content: flex-end;
        padding-bottom: 10px;
    }

    .map-wrapper .processing-changes-indicator {
        color: #fff;
        line-height: 30px;
        padding-right: 10px;
        float: right;
    }

    .map-wrapper > .map-controls {
        display: none;
    }

    .map-wrapper.edit-is-active > .map-controls {
        display: inherit;
        overflow: hidden;
    }

    .map-wrapper .processing-changes-indicator {
        color: #fff;
        line-height: 30px;
        padding-right: 10px;
        float: right;
    }

    .map-wrapper.edit-is-active {
        position: fixed;
        left: 15px;
        right: 15px;
        top: 40px;
        bottom: 15px;
        z-index: 1000;
        overflow: hidden;
        border: 2px solid #e64e42;
        box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    }

    .map-wrapper.edit-is-active > .map-container {
        height: calc(100% - 50px);
        background-color: #fff;
        position: relative;
    }

    .map-wrapper > .map-container > .map-states,
    .map-wrapper > .map-container > .map-locations {
        display: none;
    }

    .map-wrapper.edit-is-active > .map-container > .map-states {
        display: inherit;
        position: absolute;
        right: 10px;
        top: 60px;
        max-height: 300px;
        overflow-y: auto;
        z-index: 3000;
        padding: 0px 15px;
        background-color: #fff;
        border-radius: 2px;
        min-width: 265px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    }

    .map-wrapper.edit-is-active > .map-container > .map-locations {
        display: inherit;
        position: absolute;
        right: 10px;
        top: 380px;
        max-height: 300px;
        overflow-y: auto;
        z-index: 3000;
        padding: 0px 15px;
        background-color: #fff;
        border-radius: 2px;
        min-width: 265px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    }

    .map-wrapper > .map-container > .map-canvas {
        width: 100%;
        height: 500px;
    }

    .map-wrapper.edit-is-active > .map-container > .map-canvas {
        height: 100%;
    }

    .map-states h3, .map-locations h3 {
        margin: 15px 0 5px 0;
        text-transform: uppercase;
        font-size: 1.3rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .map-states ul, .map-locations ul {
        padding: 5px 0 5px;
    }

    .map-states ul li, .map-locations ul li {
        display: block;
        margin-top: 10px;
        list-style-type: none;
        border-bottom: 1px solid #e7e7e7;
    }
</style>